// Global variables for label and polygon tracking
import { LABELS, POLYGONS, VARS } from "../Global/variables";
import { createLabelDiv } from "../Global/functions";
import { Vector3 } from "three";

export const FEET_PER_METER = 1 / 0.3048;

export function formatFeetAndInches(distanceInMeters) {
    const totalDistanceInFeet = distanceInMeters * FEET_PER_METER;
    const feet = Math.floor(totalDistanceInFeet);
    const inches = Math.round((totalDistanceInFeet - feet) * 12);
    const displayFeet = inches === 12 ? feet + 1 : feet;
    const displayInches = inches === 12 ? 0 : inches;

    return {
        totalDistanceInFeet,
        text: `${displayFeet}'${displayInches}"`,
    };
}

/**
 * Creates a measurement label between two points.
 * 
 * @param {Vector3} point1 - Starting point.
 * @param {Vector3} point2 - Ending point.
 * @param {number} i - Index for label ID.
 * @param {THREE.Scene} scene - Scene to add the label to.
 * @returns {object} Measurement data with ID and value.
 */
export const createMeasurementLabels = (point1, point2, i, scene) => {
    const distanceInMeters = point1.distanceTo(point2);
    const { totalDistanceInFeet, text } = formatFeetAndInches(distanceInMeters);

    const measureId = `${VARS.measurementLabelCounter}_${i}`;
    const label = indexToLabel(i).toLowerCase();

    // Create label and position it at the midpoint
    let measurementLabel = createLabelDiv('measurementLabel', `${label}: ${text}`);
    measurementLabel.position.lerpVectors(point1, point2, 0.5);
    measurementLabel.visible = false;

    LABELS.measurements[measureId] = measurementLabel;
    scene.add(measurementLabel);

    return {
        point1: point1,
        point2: point2,
        id: measureId,
        measurement: totalDistanceInFeet,
    };
};

/**
 * Creates a name label for a segment at a given position.
 * 
 * @param {Vector3} position - The label's anchor position.
 * @returns {THREE.CSS2DObject} The created label object.
 */
export function createNameLabel(position) {
    const label = indexToLabel(POLYGONS.length);
    let segmentLabel = createLabelDiv('SegmentLabel', label);
    segmentLabel.position.lerpVectors(position, position, 0.03); // Slight offset
    return segmentLabel;
}

/**
 * Converts an index to an alphabetic label (e.g., 0 => A, 26 => AA).
 * 
 * @param {number} index - Numeric index to convert.
 * @returns {string} Alphabetic label.
 */
export function indexToLabel(index) {
    if (index < 0) return "";
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let result = "";
    do {
        const remainder = index % 26;
        result = alphabet.charAt(remainder) + result;
        index = Math.floor(index / 26) - 1;
    } while (index >= 0);
    return result;
}

/**
 * Updates a label's position and text when a vertex is moved.
 * 
 * @param {object} event - The event from vertex drag or update.
 * @param {Vector3} center - New center position of the polygon.
 */
export function updateLabelPositions(event, center) {
    const parentId = event.object.parent.uuid;

    for (const polygon of POLYGONS) {
        if (polygon.shape.uuid === parentId) {
            for (const line of polygon.edges) {
                const marker1UUID = line.vertexId1;
                const marker2UUID = line.vertexId2;

                // Check if one of the line endpoints matches the dragged vertex
                if (marker1UUID === event.object.uuid || marker2UUID === event.object.uuid) {
                    const id = line.id;
                    const isMarker1 = marker1UUID === event.object.uuid;

                    const markerPosition = isMarker1 ? center : line.point1;
                    const otherMarkerPosition = isMarker1 ? line.point2 : center;

                    // Update line point references
                    line.point1 = isMarker1 ? center : line.point1;
                    line.point2 = isMarker1 ? line.point2 : center;

                    // Recalculate and update label
                    const distanceInMeters = markerPosition.distanceTo(otherMarkerPosition);
                    const { totalDistanceInFeet, text } = formatFeetAndInches(distanceInMeters);
                    const label = LABELS.measurements[id];

                    // Preserve the existing edge-name prefix (e.g. "a: ") so it
                    // isn't dropped when the vertex is dragged.
                    const currentText = label.element.innerText || "";
                    const parts = currentText.split(":");
                    const prefix = parts.length > 1 ? parts[0] + ": " : "";

                    label.element.innerText = `${prefix}${text}`;
                    label.position.lerpVectors(markerPosition, otherMarkerPosition, 0.5);
                    line.measurement = totalDistanceInFeet;
                }
            }

            // Recenter the polygon label
            polygon.shape.geometry.computeBoundingBox();
            center = new Vector3();
            polygon.shape.geometry.boundingBox.getCenter(center);
            polygon.label.position.lerpVectors(center, center, 0.01);
            break;
        }
    }
}

/**
 * Updates all segment labels to match their current index (e.g., A, B, C).
 */
export function updateSegmentLabel() {
    let polygons = POLYGONS;
    for (let index in polygons) {
        polygons[index].label.element.innerText = indexToLabel(index);
    }
}
